import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type BadgeTone =
  | 'neutral'
  | 'good'
  | 'warn'
  | 'bad'
  | 'info'
  | 'cool'
  | 'accent'
  | 'ink'

export type BadgeSize = 'sm' | 'md'

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  bad: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  cool: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  accent: 'bg-orange-50 text-orange-700 border-orange-200',
  ink: 'bg-zinc-900 text-white border-zinc-900',
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-[7px] py-px text-[10.5px]',
  md: 'px-[10px] py-[3px] text-xs',
}

type BadgeProps = {
  children: ReactNode
  tone?: BadgeTone
  size?: BadgeSize
  className?: string
}

export function Badge({ children, tone = 'neutral', size = 'sm', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-semibold',
        toneClasses[tone],
        sizeClasses[size],
        className,
      )}
    >
      {children}
    </span>
  )
}

const stateColors: Record<string, string> = {
  enabled: 'bg-emerald-100 text-emerald-800',
  paused: 'bg-amber-100 text-amber-800',
  archived: 'bg-zinc-100 text-zinc-500',
}

const adTypeColors: Record<string, string> = {
  sp: 'bg-blue-100 text-blue-800',
  sb: 'bg-orange-100 text-orange-800',
  sd: 'bg-purple-100 text-purple-800',
}

const adTypeLabels: Record<string, string> = {
  sp: 'SP',
  sb: 'SB',
  sd: 'SD',
}

export function StateBadge({ state }: { state: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize', stateColors[state] ?? 'bg-zinc-100 text-zinc-500')}>
      {state}
    </span>
  )
}

export function AdTypeBadge({ adType }: { adType: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold', adTypeColors[adType] ?? 'bg-zinc-100 text-zinc-500')}>
      {adTypeLabels[adType] ?? adType.toUpperCase()}
    </span>
  )
}

export function MatchTypeBadge({ matchType }: { matchType: string }) {
  const colors: Record<string, string> = {
    broad: 'bg-sky-100 text-sky-800',
    phrase: 'bg-indigo-100 text-indigo-800',
    exact: 'bg-teal-100 text-teal-800',
  }
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize', colors[matchType] ?? 'bg-zinc-100 text-zinc-500')}>
      {matchType}
    </span>
  )
}
